import { Component, DebugElement, OnInit, ViewChild } from "@angular/core";
import { MatSortHeader } from "@angular/material/sort";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { GS1ColumnDef, GS1RowData, GS1TableComponent } from "@gs1france/gs1-components-library";
import { StorageService } from "src/app/core/services/storage/storage.service";
import { GLN, GLNInterface } from "src/app/models/gln.model";
import { GLNService } from "../../../core/services/gln/gln.service";
import { ContactInfo } from "src/app/models/contact.model";
import { ContactService } from "src/app/core/services/contact/contact.service";
import { ThemePalette } from '@angular/material/core';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'account-gln',
  templateUrl: './account-gln.component.html',
  styleUrls: ['./account-gln.component.scss'],
})

export class AccountGLNComponent implements OnInit {
  dataTable: any[] = [];
  columns!: GS1ColumnDef[];
  loading = true;
  exportLoading = false;
  admin = false;
  glns: GLNInterface[] = [];  
  contactInfo!: ContactInfo;
  totalAuthorizedCodeRange: number = 0;
  totalAuthorizedUsedCode: number = 0;
  isWarning: boolean = false;
  color: ThemePalette = 'primary';
  mode: ProgressSpinnerMode = 'determinate';
  value = 0; 

  @ViewChild(GS1TableComponent) gs1Table!: GS1TableComponent<any>;

  constructor(
    private glnService: GLNService,
    private _storage: StorageService,
    private sanitized: DomSanitizer,
    private router: Router,
    private contactService: ContactService
    ) {}

  urlNewCodeOrder = environment.URL_NEW_CODE_ORDER;

  /**
   * Initializes the data table and sets up the data table to display the given data.
   * @returns None
   */
  async ngOnInit() {
    try {
      await Promise.all([this.loadTable(), this.loadProfile()]);
      this.setupDataTable(this.glns);
      this.setOldDatatableState();
      this.loading = false;
    } catch (err) {
      console.error(err);
    }

    this.totalAuthorizedCodeRange = await this.computeRangeWithGCP();
    this.totalAuthorizedUsedCode = this.glns.length
    if(this.totalAuthorizedCodeRange > 0 && this.totalAuthorizedUsedCode > 0) {
      this.value = ((100 * this.totalAuthorizedUsedCode)/this.totalAuthorizedCodeRange);
    }
    this.isWarning = this.value >= 95 ? true : false;
  }
 
  /**
   * Loads the GLN table from the database.
   * @returns None
   */
  async loadTable() {
    this.setUpColumns();
    this.glns = await this._storage.getGLNs();
    //this.gs1Table.currentPage = this._storage.getAccountPage();

  }

  setOldDatatableState() {
    const prevState = this._storage.getAccountPage();
      if (prevState.search.length > 0) {
        this.gs1Table.dataSource.filter = prevState.search;
        this.gs1Table.currentSearch = prevState.search
      }
      this.gs1Table.sort.sort({ id: '', start: 'desc', disableClear: false });
      this.gs1Table.sort.direction = prevState.sortDirection === '' ? 'desc': prevState.sortDirection;
      this.gs1Table.sort.sort({ id: prevState.sortBy, start: prevState.sortDirection === '' ? 'desc': prevState.sortDirection, disableClear: false });
      (this.gs1Table.sort.sortables.get(prevState.sortBy) as MatSortHeader)._setAnimationTransitionState({ toState: 'active' });
      this.gs1Table.switchPage(prevState.page)
  }

  /**
   * Loads the user's profile from storage.
   * @returns None
   */
  async loadProfile() {
    this.admin = await this._storage.isAdmin();
  }

  /**
   * Exports the current user's GLNs to a file.
   * @returns None
   */
  async export(){
    try {
      this.exportLoading = true;
      await this.glnService.export(this.glns.filter(gln => gln.status !== 'DRAFT'));
    } finally {
      this.exportLoading = false;
    }
  }

  /**
   * Takes in an array of GLN objects and creates a data table for the table component.
   * @param {GLNInterface[]} glns - the array of GLN objects
   * @returns None
   */
  setupDataTable(glns: GLNInterface[]) {
    this.dataTable = glns.map(glnLike => {
      const gln = new GLN(glnLike);
      gln.types = [];
      for (let i=0; i< gln.glnTypes.length; i++) {
        if (gln.glnTypes[i] === 'LEGAL_ENTITY') {
          gln.types.push({value: GLN.getLabelForType('LEGAL_ENTITY'), color: 'mint'})
        } else if (gln.glnTypes[i] === 'FIXED_PHYSICAL_LOCATION') {
          gln.types.push({value: GLN.getLabelForType('FIXED_PHYSICAL_LOCATION'), color: 'honey'})
        } else if (gln.glnTypes[i] === 'FUNCTION') {
          gln.types.push({value: GLN.getLabelForType('FUNCTION'), color: 'purple'})
        } else if (gln.glnTypes[i] === 'DIGITAL_LOCATION') {
          gln.types.push({value: GLN.getLabelForType('DIGITAL_LOCATION'), color: 'lime'})
        }
      }
      gln.actions = (gln.isActive === false || gln.isMain || !this.admin) ? [] : ['pencil'];
      gln.publishedState = this.getStatusContent(gln);
      if (gln.isMain) {
        gln.class = 'bold'
      }
      return gln
    });
  }

  /**
   * Sets up the columns for the table.
   * @returns None
   */
  setUpColumns() {
    this.columns = [
      {key: 'isMain', dataType: 'flag', 'iconForFlag': 'house-door', width: 1, color: 'blue',  'hoverMessage': 'GLN principal de l’entité/ entreprise.', 'hoverPosition':'right'},
      {key: 'name', displayName: 'Nom', dataType: 'string', width: 6, color: 'blue'},
      {key: 'gln', displayName: 'Code GLN', dataType: 'string', width: 4},
      {key: 'types', displayName: 'Type(s)', dataType: 'chips', width: 7},
      {key: 'publishedState', displayName: 'État', dataType: 'html', width: 3, color: this.getStatusColor},
      {key: 'lastModified', dataType: 'date', displayName: 'Dernière modification', width: 4, dateFormat: 'dd/MM/yy - HH:mm'},
      {key: 'actions', dataType: 'actions', displayName: 'Modifier', width: 1, align: 'right', size: '110px'}
    ];
  }

  /**
   * Returns the HTML content for the status column of the GLN table.
   * @param {GLN} gln - the GLN object to get the status content for.
   * @returns {SafeHtml} - the HTML content for the status column of the GLN table.
   */
  getStatusContent(gln: GLN): SafeHtml {
    let html = '';
    if (gln.status === 'DRAFT') html = '<span style="font-style:italic;font-weight:300">Brouillon</span>';
    else if (gln.isPublished === true) html = '<i class="bi bi-cloud-check" style=\'font-size:1.4em;line-height:1\'></i>Publié';
    else html = '<i class="bi bi-cloud-slash" style=\"font-size:1.4em;line-height:1\"></i>Non publié';
    return this.sanitized.bypassSecurityTrustHtml(html)
  }

  /**
   * Returns the color of the status of the given row.
   * @param {GS1ColumnDef} column - the column definition object
   * @param {GS1RowData} row - the row data object
   * @returns {string} - the color of the status of the given row.
   */
  getStatusColor = (column: GS1ColumnDef, row: GS1RowData) => {
    if (!row.status) return '';
    if (row.status === 'DRAFT') return 'ui-6';
    if (row.isPublished === true) return 'forest';
    return 'peach';
  }

  /**
   * Function to handle the click event of a table content cell.
   * @param {any} $event - the event object.
   * @returns None
   */
  contentClicked($event: {element: any, column: any, action?: string}) {
    this.saveState();
    this.router.navigateByUrl('mes-gln/details/modifier/'+$event.element.id);
  }

  /**
   * Function to handle the click event of a table row.
   * @param {any} $event - the event object.
   * @returns None
   */
  rowClicked(element: any) {
    this.saveState();
    this.router.navigateByUrl('mes-gln/details/'+element.id);
  }

  sortingDataAccessor: (data: {}, property: string) => string | number = (item: any, property): string | number => {
    switch (property) {
      case 'isMain': {
        return item.isMain === true ? item.gln : item.lastModified;
      }
      default: {
        return item[property];
      }
    }
  }

  saveState() {
    this._storage.saveAccountPage(this.gs1Table.currentPage, this.gs1Table.sort.active, this.gs1Table.sort.direction, this.gs1Table.currentSearch)
  }

  async computeRangeWithGCP(): Promise<number> {
    this.contactInfo = await this.contactService.getInfoContact();
    var rangeGCP :number[] = [1,10,100,1000,10000,100000,1000000,10000000,100000000,1000000000,10000000000,100000000000];
    let tempComputeRange = 0;  
    if(this.contactInfo?.gcps.length > 0) {      
      this.contactInfo?.gcps.map(function(gcp){
        if(gcp) {
          tempComputeRange += rangeGCP[12 - gcp.key.length];
        }     
      })
    }
    return tempComputeRange;
  }

//format range number , to  ' ';
 formatGlnCount(range:number) {
  return range.toLocaleString().replace(',', ' ').replace('.', ' ');
} 

}
